import { useCallback, useEffect, useMemo, useState } from "react";
import { useCharacterizationSites } from "../../characterization-2/characterizationArea/useCharacterizationSites";
import { getListRotationPeriods } from "../../../services/container";
import { getMonthsAgo } from "../../../components/formatted/FormattedDateAndTime";
import { getCharacterizationStatus } from "../../characterization-2/core/utils/utils";

const SINCE_BEGINNING_START = new Date(2021, 3, 26);

const parseClient = (payload) => {
  if (!payload) return { name: "-", address: "-" };
  try {
    return JSON.parse(payload);
  } catch {
    return { name: "-", address: "-" };
  }
};

export const useClientCharacterizationHistory = (
  clientId,
  { periodMonths = 6, statusFilter = "all", zoneFilter = "all" } = {},
) => {
  const {
    sites,
    isLoading: sitesLoading,
    containers,
    superContainers,
  } = useCharacterizationSites();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!clientId || sites.length === 0) {
      setRows([]);
      return;
    }
    setIsLoading(true);
    try {
      const now = new Date();
      const startDate =
        periodMonths === null
          ? new Date(SINCE_BEGINNING_START)
          : getMonthsAgo(now, periodMonths);
      const start = startDate.toISOString();
      const stop = now.toISOString();

      const results = await Promise.allSettled(
        sites.map(async (site) => {
          const response = await getListRotationPeriods(
            {
              periodMaterialStart: start,
              periodMaterialStop: stop,
              superContainerId: site.superContainerId ?? null,
              isCharacterization: true,
            },
            site._id,
          );
          const periods = response?.data?.periods ?? [];
          return periods
            .filter(
              (p) =>
                p.mongoClientId && String(p.mongoClientId) === String(clientId),
            )
            .map((period) => {
              let wasteCategory = null;
              if (
                period.waste_category &&
                String(period.waste_category).trim() !== ""
              ) {
                try {
                  wasteCategory = JSON.parse(period.waste_category);
                } catch {
                  wasteCategory = null;
                }
              }
              return {
                ...period,
                periodMaterialStartSubfilter: period.start,
                periodMaterialStopSubfilter: period.stop,
                zoneSite: site,
                superContainerId: site.superContainerId ?? null,
                clientParsed: parseClient(period.client),
                wasteCategory,
                status: getCharacterizationStatus(period),
                startDate: period.start ? new Date(period.start) : null,
                lastUpdateDate: period.lastUpdate
                  ? new Date(period.lastUpdate)
                  : period.stop
                    ? new Date(period.stop)
                    : null,
              };
            });
        }),
      );

      const merged = results
        .filter((r) => r.status === "fulfilled")
        .flatMap((r) => r.value);
      setRows(merged);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to load client characterization history", error);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, sites, periodMonths]);

  useEffect(() => {
    if (!sitesLoading) {
      fetchHistory();
    }
  }, [fetchHistory, sitesLoading]);

  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        if (statusFilter !== "all" && row.status !== statusFilter) {
          return false;
        }
        if (
          zoneFilter !== "all" &&
          String(row.zoneSite?._id) !== String(zoneFilter)
        ) {
          return false;
        }
        return true;
      }),
    [rows, statusFilter, zoneFilter],
  );

  const zoneOptions = useMemo(
    () =>
      sites.map((site) => ({
        value: site._id,
        label: site.addressName || site.name || site._id,
        positioning: site.positioning,
      })),
    [sites],
  );

  return {
    rows: filteredRows,
    isLoading: isLoading || sitesLoading,
    refetch: fetchHistory,
    zoneOptions,
    containers: containers ?? [],
    superContainers: superContainers ?? [],
  };
};
